/**
 * AUTH-002 §6.1 items 2a + 4 — the one way the browser calls the app's own `/api/*` routes.
 *
 *   - Before sending: the controller's guard (idle check, then ≥ 60 s of token life, or
 *     more for long requests). A session that has ended is not sent at all.
 *   - The current access token goes in `Authorization: Bearer`, so routes authenticate the
 *     same way whether the session lives in a cookie or (embedded) in localStorage.
 *   - On a 401: refresh once and resend once. Every route's 401 is its authentication
 *     gate, which runs before any write, so a resend cannot double-apply anything. If the
 *     refresh token is dead the user is signed out to `/login?reason=expired`; if the
 *     resend is still refused as SESSION_EXPIRED, likewise. Any other 401 is returned.
 *   - SEC-003 Phase 2b (I2): a 403 ACCOUNT_INACTIVE ends the session at once (reason
 *     `inactive`, the login page says the account is inactive) — no refresh, no resend. A 403
 *     PROFILE_NOT_AUTHORIZED raises the auth notice (AuthNoticeBanner shows the message) and
 *     keeps the session. Either way the 403 itself is returned to the caller.
 *   - SEC-003 Interim: a 503 PHI_NOT_ENABLED (no PHI database yet) raises the PHI notice
 *     (AuthNoticeBanner shows "Patient features are not enabled yet…"); the 503 is returned.
 *
 * Never throws for an auth reason: callers keep their `if (!res.ok)` handling. When the
 * session has ended the response is a local 401 with SESSION_EXPIRED and the "session
 * ended" wording, and the page is already on its way to /login.
 */
import type { SessionController } from './session-controller';
import { sessionEndedResponse } from './supabase-fetch';
import {
  ACCOUNT_INACTIVE_CODE,
  MIN_TOKEN_LIFE_SECONDS,
  PHI_NOT_ENABLED_CODE,
  PROFILE_NOT_AUTHORIZED_CODE,
  SESSION_EXPIRED_CODE,
} from './session-policy';

export interface AuthFetchOptions {
  /** Token life the request needs, in seconds. Long exports pass LONG_REQUEST_TOKEN_LIFE_SECONDS. */
  minTokenLifeSeconds?: number;
}

export type AuthFetch = (
  input: string,
  init?: RequestInit,
  options?: AuthFetchOptions
) => Promise<Response>;

/** Read a response's `code` without consuming the caller's body. */
export async function responseCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: unknown };
    return typeof body?.code === 'string' ? body.code : null;
  } catch {
    return null;
  }
}

function withBearer(init: RequestInit, token: string | null): RequestInit {
  if (!token) return init;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

export function createAuthFetch(deps: {
  controller: () => SessionController | null;
  fetch: typeof fetch;
  /** SEC-003 I2.2: called once per 403 PROFILE_NOT_AUTHORIZED (the runtime raises AUTH_NOTICE_EVENT). */
  onProfileNotAuthorized?: () => void;
  /** SEC-003 Interim: called once per 503 PHI_NOT_ENABLED (the runtime raises PHI_NOT_ENABLED_EVENT). */
  onPHINotEnabled?: () => void;
}): AuthFetch {
  /** SEC-003 I2 + Interim: act on a 403 account refusal or a 503 PHI_NOT_ENABLED. Returns the response unchanged. */
  async function onRefusal(res: Response, controller: SessionController): Promise<Response> {
    if (res.status === 503) {
      if ((await responseCode(res)) === PHI_NOT_ENABLED_CODE) deps.onPHINotEnabled?.();
      return res;
    }
    if (res.status !== 403) return res;
    const code = await responseCode(res);
    if (code === ACCOUNT_INACTIVE_CODE) {
      await controller.endSession('inactive');
    } else if (code === PROFILE_NOT_AUTHORIZED_CODE) {
      deps.onProfileNotAuthorized?.();
    }
    return res;
  }

  return async function authFetch(input, init = {}, options = {}) {
    const controller = deps.controller();
    // The session ended in this tab and the page is on its way to /login: send nothing.
    if (controller?.isEnding()) return sessionEndedResponse();
    // No managed session (server render, or not signed in): an ordinary fetch.
    if (!controller || !controller.isActive()) return deps.fetch(input, init);

    const pre = await controller.ensureFresh(options.minTokenLifeSeconds ?? MIN_TOKEN_LIFE_SECONDS);
    if (pre.status === 'ended') return sessionEndedResponse();
    // 'unavailable': refresh could not be attempted right now. Send anyway; the cookie
    // (or a still-valid token) may be accepted, and a 401 below gets one more try.
    const token = pre.status === 'ok' ? pre.token : null;

    const res = await deps.fetch(input, withBearer(init, token));
    if (res.status !== 401) return onRefusal(res, controller);

    const fresh = await controller.refreshAfter401(token);
    if (fresh.status === 'ended') return sessionEndedResponse();
    if (fresh.status !== 'ok') return res;

    const retry = await deps.fetch(input, withBearer(init, fresh.token));
    if (retry.status === 401 && (await responseCode(retry)) === SESSION_EXPIRED_CODE) {
      await controller.endSession('expired', { revoke: false });
      return sessionEndedResponse();
    }
    return onRefusal(retry, controller);
  };
}
